import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Users, TrendingUp, Star, Eye, EyeOff, Trophy } from 'lucide-react';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import PRCelebration from './PRCelebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Whiteboard({ user }) {
  const [todayWODs, setTodayWODs] = useState([]);
  const [todayClasses, setTodayClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState({});
  const [ratingSummaries, setRatingSummaries] = useState({});
  const [submittedRatings, setSubmittedRatings] = useState({});
  const [showWodDetails, setShowWodDetails] = useState(false);
  const [prLogDialog, setPrLogDialog] = useState(null);
  const [prLogValue, setPrLogValue] = useState('');
  const [prLogNotes, setPrLogNotes] = useState('');
  const [celebration, setCelebration] = useState(null);
  const [movements, setMovements] = useState([]);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchData();
    axios.get(`${API}/movements`).then(r => setMovements(r.data)).catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      const [wodsRes, classesRes] = await Promise.all([
        axios.get(`${API}/wods/date/${today}`),
        axios.get(`${API}/classes`)
      ]);
      setTodayWODs(wodsRes.data);
      const todaysClasses = classesRes.data.filter(c => c.date === today);
      setTodayClasses(todaysClasses);

      // Fetch rating summaries for each WOD
      if (wodsRes.data.length > 0) {
        const summaries = {};
        const existingRatings = {};
        const submitted = {};
        await Promise.all(wodsRes.data.map(async (wod) => {
          try {
            const res = await axios.get(`${API}/wods/${wod.id}/ratings-summary`);
            summaries[wod.id] = res.data;
            if (res.data.user_rating !== null) {
              existingRatings[wod.id] = res.data.user_rating;
              submitted[wod.id] = true;
            }
          } catch {}
        }));
        setRatingSummaries(summaries);
        setRatings(prev => ({ ...prev, ...existingRatings }));
        setSubmittedRatings(submitted);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = async (wodId, value) => {
    setRatings({ ...ratings, [wodId]: value });
  };

  const submitRating = async (wodId) => {
    try {
      await axios.post(`${API}/ratings`, {
        wod_id: wodId,
        rating: ratings[wodId] || 5
      });
      setSubmittedRatings({ ...submittedRatings, [wodId]: true });
      // Refresh the summary for this WOD
      const res = await axios.get(`${API}/wods/${wodId}/ratings-summary`);
      setRatingSummaries({ ...ratingSummaries, [wodId]: res.data });
      toast.success('Rating submitted!');
    } catch (error) {
      toast.error('Failed to submit rating');
    }
  };

  const logPRFromWhiteboard = async (e) => {
    e.preventDefault();
    if (!prLogDialog) return;
    try {
      const result = await axios.post(`${API}/pr-logs`, {
        movement_id: prLogDialog.wod.pr_movement_id,
        value: parseFloat(prLogValue),
        notes: prLogNotes || null
      });
      setPrLogDialog(null);
      setPrLogValue('');
      setPrLogNotes('');
      if (result.data.beat_existing_pr) {
        setCelebration({ movementName: prLogDialog.movementName, value: parseFloat(prLogValue), unit: prLogDialog.unit });
      } else {
        toast.success(result.data.is_pr ? 'First entry logged!' : 'Score logged!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log score');
    }
  };

  const [attendees, setAttendees] = useState({});

  const fetchClassAttendees = async (classId) => {
    try {
      const response = await axios.get(`${API}/classes/${classId}/bookings`);
      return response.data;
    } catch (error) {
      toast.error('Failed to load attendees');
      return [];
    }
  };

  const [showingAttendees, setShowingAttendees] = useState(null);

  const showAttendees = async (classId) => {
    if (showingAttendees === classId) {
      setShowingAttendees(null);
      return;
    }
    const users = await fetchClassAttendees(classId);
    setAttendees({ ...attendees, [classId]: users });
    setShowingAttendees(classId);
  };

  const getDifficultyLabel = (value) => {
    if (value <= 3) return 'Easy';
    if (value <= 6) return 'Moderate';
    if (value <= 8) return 'Hard';
    return 'Brutal';
  };

  const getDifficultyColor = (value) => {
    if (value <= 3) return 'text-[#22C55E]';
    if (value <= 6) return 'text-[#CCFF00]';
    if (value <= 8) return 'text-[#F97316]';
    return 'text-[#EF4444]';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#CCFF00] text-xl font-heading uppercase">Loading...</p>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen p-6 md:p-12" data-testid="whiteboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl md:text-7xl font-heading font-black uppercase tracking-tighter text-[#FAFAFA] mb-2">
            TODAY'S BOARD
          </h1>
          <p className="text-lg text-[#A1A1AA] font-medium">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* WODs Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-heading font-bold uppercase tracking-tight text-[#FAFAFA] flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-[#CCFF00]" />
              Workout of the Day
            </h2>
            <div className="flex items-center gap-2">
              {showWodDetails ? <Eye className="w-4 h-4 text-[#A1A1AA]" /> : <EyeOff className="w-4 h-4 text-[#52525B]" />}
              <Switch
                data-testid="wod-details-toggle"
                checked={showWodDetails}
                onCheckedChange={setShowWodDetails}
                className="data-[state=checked]:bg-[#CCFF00]"
              />
            </div>
          </div>
          {todayWODs.length === 0 ? (
            <Card className="bg-[#18181B] border-[#27272A] p-8" data-testid="no-wods-message">
              <p className="text-[#A1A1AA] text-center text-lg">No WODs scheduled for today. Check back later!</p>
            </Card>
          ) : (
            <div className="space-y-6">
              {todayWODs.map((wod) => {
                const summary = ratingSummaries[wod.id];
                const currentRating = ratings[wod.id] || 5;
                const hasSubmitted = submittedRatings[wod.id];

                return (
                  <Card key={wod.id} className="bg-[#18181B] border-[#27272A] p-6 hover:border-[#3F3F46] transition-colors" data-testid="wod-card">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-2xl font-heading font-bold uppercase text-[#FAFAFA]">{wod.title}</h3>
                      {/* Community Rating Badge */}
                      {summary && summary.average !== null && (
                        <div className="flex items-center gap-2 bg-[#09090B] border border-[#27272A] rounded-sm px-3 py-1.5" data-testid={`community-rating-${wod.id}`}>
                          <Star className="w-4 h-4 text-[#CCFF00] fill-[#CCFF00]" />
                          <span className={`text-sm font-bold ${getDifficultyColor(summary.average)}`}>
                            {summary.average}
                          </span>
                          <span className="text-xs text-[#A1A1AA]">
                            / 10 ({summary.count} {summary.count === 1 ? 'vote' : 'votes'})
                          </span>
                        </div>
                      )}
                    </div>

                    {/* PR Movement Badge */}
                    {wod.pr_movement_id && wod.pr_movement_name && (
                      <div className="mb-4 flex items-center justify-between bg-[#CCFF00]/5 border border-[#CCFF00]/20 rounded-sm px-4 py-3" data-testid={`pr-badge-${wod.id}`}>
                        <div className="flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-[#CCFF00]" />
                          <div>
                            <p className="text-xs text-[#CCFF00] uppercase tracking-wider font-semibold">PR Movement</p>
                            <p className="text-sm font-bold text-[#FAFAFA]">{wod.pr_movement_name}</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            const mov = movements.find(m => m.id === wod.pr_movement_id);
                            setPrLogDialog({ wod, movementName: wod.pr_movement_name, unit: mov?.unit || '' });
                            setPrLogValue('');
                            setPrLogNotes('');
                          }}
                          data-testid={`log-score-btn-${wod.id}`}
                          className="bg-[#CCFF00] text-black font-bold text-xs uppercase hover:bg-[#b8e600] rounded-sm"
                        >
                          Log your score
                        </Button>
                      </div>
                    )}
                    {showWodDetails && (
                      <>
                        <div className="text-[#A1A1AA] whitespace-pre-wrap font-mono text-sm leading-relaxed mb-4">
                          {wod.description}
                        </div>

                    {/* Average Difficulty Bar */}
                    {summary && summary.average !== null && (
                      <div className="mb-4 p-3 bg-[#09090B] rounded-sm border border-[#27272A]" data-testid={`difficulty-bar-${wod.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-[#A1A1AA] uppercase tracking-wide">Community Difficulty</span>
                          <span className={`text-sm font-bold ${getDifficultyColor(summary.average)}`}>
                            {getDifficultyLabel(summary.average)}
                          </span>
                        </div>
                        <div className="w-full bg-[#27272A] rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-500"
                            style={{
                              width: `${(summary.average / 10) * 100}%`,
                              backgroundColor: summary.average <= 3 ? '#22C55E' : summary.average <= 6 ? '#CCFF00' : summary.average <= 8 ? '#F97316' : '#EF4444'
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {(user.role === 'athlete' || user.role === 'coach' || user.role === 'head_coach') && (
                      <div className="mt-6 p-4 bg-[#09090B] rounded-sm border border-[#27272A]" data-testid="rating-section">
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-semibold text-[#FAFAFA] uppercase tracking-wide">
                            {hasSubmitted ? 'Your Rating' : 'How difficult was this WOD?'}
                          </label>
                          {hasSubmitted && (
                            <span className="text-xs text-[#22C55E] font-semibold uppercase tracking-wide" data-testid={`rated-badge-${wod.id}`}>
                              Rated
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-[#A1A1AA] uppercase">Easy</span>
                          <Slider
                            data-testid={`rating-slider-${wod.id}`}
                            value={[currentRating]}
                            onValueChange={(value) => handleRatingChange(wod.id, value[0])}
                            max={10}
                            min={1}
                            step={1}
                            className="flex-1"
                          />
                          <span className="text-xs text-[#A1A1AA] uppercase">Hard</span>
                          <span className={`text-lg font-bold w-8 text-center ${getDifficultyColor(currentRating)}`}>
                            {currentRating}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-4">
                          <span className={`text-xs font-semibold ${getDifficultyColor(currentRating)}`}>
                            {getDifficultyLabel(currentRating)}
                          </span>
                          <Button
                            onClick={() => submitRating(wod.id)}
                            data-testid={`submit-rating-${wod.id}`}
                            className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600] rounded-sm px-6"
                          >
                            {hasSubmitted ? 'Update Rating' : 'Submit Rating'}
                          </Button>
                        </div>
                      </div>
                    )}
                      </>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Today's Classes */}
        <div>
          <h2 className="text-3xl font-heading font-bold uppercase tracking-tight text-[#FAFAFA] mb-6 flex items-center gap-3">
            <Users className="w-8 h-8 text-[#CCFF00]" />
            Today's Classes
          </h2>
          {todayClasses.length === 0 ? (
            <Card className="bg-[#18181B] border-[#27272A] p-8" data-testid="no-classes-message">
              <p className="text-[#A1A1AA] text-center text-lg">No classes scheduled for today.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {todayClasses.map((classSession) => (
                <Card key={classSession.id} className="bg-[#18181B] border-[#27272A] p-4" data-testid="class-card">
                  <h3 className="text-xl font-heading font-bold uppercase text-[#FAFAFA] mb-2">
                    {classSession.title}
                  </h3>
                  <p className="text-[#A1A1AA] text-sm mb-3">
                    {classSession.start_time} - {classSession.end_time}
                  </p>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-[#CCFF00]" />
                    <span className="text-[#FAFAFA] font-semibold">
                      {classSession.booked_count} / {classSession.max_capacity}
                    </span>
                    <span className="text-[#A1A1AA] text-sm">Booked</span>
                  </div>
                  <Button
                    onClick={() => showAttendees(classSession.id)}
                    data-testid={`show-attendees-${classSession.id}`}
                    variant="outline"
                    size="sm"
                    className="w-full text-xs uppercase tracking-wide border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#CCFF00]"
                  >
                    {showingAttendees === classSession.id ? 'Hide' : 'View'} Attendees
                  </Button>
                  {showingAttendees === classSession.id && attendees[classSession.id] && (
                    <div className="mt-3 p-3 bg-[#09090B] rounded-sm border border-[#27272A]" data-testid="attendees-list">
                      {attendees[classSession.id].length === 0 ? (
                        <p className="text-[#A1A1AA] text-xs">No one booked yet</p>
                      ) : (
                        <ul className="space-y-1">
                          {attendees[classSession.id].map((attendee) => (
                            <li key={attendee.booking_id || attendee.id} className="text-[#FAFAFA] text-sm">
                              {attendee.user ? attendee.user.name : attendee.name}
                              {attendee.guest_count > 0 && (
                                <span className="text-xs text-[#A1A1AA]"> (+ {attendee.guest_count} guest{attendee.guest_count > 1 ? 's' : ''})</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>

    {/* PR Log Dialog (from whiteboard) */}
    <Dialog open={!!prLogDialog} onOpenChange={(open) => { if (!open) { setPrLogDialog(null); setPrLogValue(''); setPrLogNotes(''); } }}>
      <DialogContent className="bg-[#18181B] border-[#27272A] max-w-sm" data-testid="whiteboard-pr-log-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#FAFAFA] font-heading uppercase text-lg">
            Log Score
          </DialogTitle>
          {prLogDialog && (
            <p className="text-sm text-[#CCFF00] font-semibold">{prLogDialog.movementName}</p>
          )}
        </DialogHeader>
        <form onSubmit={logPRFromWhiteboard} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Your Score</Label>
            <Input
              data-testid="whiteboard-pr-value"
              type="number"
              step="any"
              value={prLogValue}
              onChange={(e) => setPrLogValue(e.target.value)}
              required
              placeholder="e.g. 100"
              className="bg-[#09090B] border-[#27272A] text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Notes (optional)</Label>
            <Input
              data-testid="whiteboard-pr-notes"
              value={prLogNotes}
              onChange={(e) => setPrLogNotes(e.target.value)}
              placeholder="e.g. Felt strong today"
              className="bg-[#09090B] border-[#27272A] text-white"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPrLogDialog(null)} className="border-[#27272A] text-[#A1A1AA]">Cancel</Button>
            <Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#b8e600] font-bold uppercase">Log</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    {/* PR Celebration */}
    {celebration && (
      <PRCelebration
        open={!!celebration}
        onDismiss={() => setCelebration(null)}
        movementName={celebration.movementName}
        value={celebration.value}
        unit={celebration.unit}
      />
    )}
    </>
  );
}
